// @mui
import { Alert, Box } from '@mui/material'
import {
  CustomContentProps,
  SnackbarProvider,
  SnackbarProviderProps,
  VariantType,
  useSnackbar,
} from 'notistack'
import { ReactNode, forwardRef } from 'react'
//
import { TOAST_TONE } from 'src/design-system'
import { motion as motionTokens } from 'src/theme/tokens.generated'
import HintDismiss from './HintDismiss'

// ----------------------------------------------------------------------
// A toast is a hint that leaves.
//
// **Every rule this file used to make was dead.** It styled notistack v2's
// class names against notistack 3.0.2: v3 emits `.notistack-SnackbarContainer`,
// `.notistack-Snackbar`, `.notistack-MuiContent` and
// `.notistack-MuiContent-<variant>`, and the strings `.SnackbarContent-root`
// and `.SnackbarItem-…` occur nowhere in the package. So a `GlobalStyles`
// block naming the paper fill, the radius, the shadow and the ink selected
// nothing, and every toast in the console kept notistack's own hard-coded
// fills — `#2196f3` info, `#43a047` success, `#d32f2f` error, `#ff9800`
// warning. On top of that a hand-rolled mark painted `palette[tone].main` at
// sixteen percent alpha over the same saturated hue it was supposed to sit on,
// which is why the info and success marks could not be seen at all. None of it
// was a version bump anybody noticed: a stylesheet that matches nothing throws
// nothing.
//
// **So the fix is to stop keeping a second tone treatment.** A toast says the
// same five kinds of thing a hint says, and the console already has a
// component for that — the themed `Alert`. notistack v3 takes a component per
// variant through `Components`, so each variant renders
// `<Alert severity={TOAST_TONE[variant]} variant="standard">`: the fill is the
// tone's `soft`, the copy and the mark are its `text`, the glyph is the
// theme's own `iconMapping`, the one inset and the mark centred on the copy's
// line come for free, and a change to any of that reaches a toast and a hint
// together or reaches neither. `TOAST_TONE` is the only mapping, and it lives
// beside `ALERT_TONES` rather than here, because which tone a word means is a
// design fact and this is a provider.
//
// **The dismiss is inside the alert, and the alert is the whole toast.** It
// used to be a sibling, in a box of its own beside the alert, on the reading
// that `scripts/conformanceRules.mjs` fails an info alert containing a button
// — if there is something to do, it is not a footnote. The reading was right
// about actions and wrong about this control, and what it drew said so: the
// tinted fill stopped where the sentence stopped and the × sat on a bare white
// strip at the right-hand end, so a green success toast was two thirds green
// and one third paper. A surface with two grounds reads as a surface that has
// failed to load.
//
// Closing a notice is not something to do about its subject, so the rule now
// excludes the one control that does it — by class, so a `Button` or a link in
// an info alert is still reported. The corner it sits in is the theme's, the
// same one every dismissible hint in the console uses.
//
// **The surface under it is `background.paper`, and it has to be opaque.** In
// dark mode every soft tint is an alpha wash authored to composite over a
// card, so a toast with a transparent ground would show the screen through its
// own fill, and would show a different colour over every screen. The paper is
// also what the tone's ink and mark are measured against by the contrast
// tests, so the toast is drawn on the ground those measurements assume. It is
// a box under the alert rather than a colour on it: the alert paints its own
// tint, and the two have to be separate layers for the wash to composite.
// ----------------------------------------------------------------------

/**
 * One toast: the themed hint, and the control that sends it away.
 *
 * A `forwardRef` on the outermost node is notistack's requirement rather than
 * a nicety — the slide transition clones this element and drives the
 * transform on the node the ref resolves to, so a component that swallows it
 * renders a toast that never moves. `style` is the same contract: the
 * transition writes into it and hands it back through props.
 *
 * The dismiss is a plain `IconButton` and not `IconButtonAnimate`. The
 * provider sits *above* `MotionLazyContainer` in `App`, so anything it renders
 * itself is outside the `LazyMotion` boundary and a framer `m.div` there loads
 * no features: the animated wrapper has been drawing a plain button all along,
 * with a motion component's cost.
 */
const Toast = forwardRef<HTMLDivElement, CustomContentProps>(function Toast(
  { id, message, action, variant, hideIconVariant, style, className },
  ref,
) {
  const { closeSnackbar } = useSnackbar()
  const tone = TOAST_TONE[variant]

  // notistack allows an action per snack. Nothing in the console passes one
  // today and the tone that may carry one, `attention`, is not a tone a toast
  // can take — but a prop that is accepted and silently dropped is how the
  // last defect in this file lasted a major version, so it goes to the alert's
  // own action slot rather than being thrown away. That is the row under the
  // copy, which is where a hint's remedy is drawn everywhere else.
  const remedy = typeof action === 'function' ? action(id) : action

  return (
    <Box
      ref={ref}
      className={className}
      style={style}
      sx={(theme) => ({
        display: 'flex',
        maxWidth: '100%',
        // `wordBreak: 'break-all'`, which this used to carry, breaks a word
        // wherever the line ends whether or not it needed breaking. `anywhere`
        // breaks only what has nowhere else to break, which is the case it was
        // put there for: a toast naming a vehicle or an address.
        overflowWrap: 'anywhere',
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.radii.control,
        boxShadow: theme.customShadows.z8,
        // The tint runs to the corner. Without it the alert paints a square
        // fill inside a rounded box and the radius shows as four pale notches.
        overflow: 'hidden',
      })}
    >
      <Alert
        severity={tone}
        variant="standard"
        icon={hideIconVariant ? false : undefined}
        // The alert *is* the toast, so it takes the whole box: the ground
        // behind it exists to composite the tint, not to be seen beside it.
        sx={{ flexGrow: 1, minWidth: 0, borderRadius: 0 }}
        action={remedy}
      >
        {message}
        <HintDismiss label="Dismiss" onClick={() => closeSnackbar(id)} />
      </Alert>
    </Box>
  )
})

/**
 * The same component for every variant.
 *
 * notistack keys this map by variant and the component reads its own
 * `variant`, so registering one component five times is not a shortcut — it is
 * what keeps the five toasts one thing. The keys come off `TOAST_TONE`, so a
 * variant that exists in notistack and not in the record cannot reach a screen
 * with no component behind it; it fails to compile in the record first.
 */
const TOAST_COMPONENTS = Object.fromEntries(
  (Object.keys(TOAST_TONE) as VariantType[]).map((variant) => [variant, Toast]),
) as SnackbarProviderProps['Components']

// ----------------------------------------------------------------------

type Props = {
  children: ReactNode
}

export default function NotistackProvider({ children }: Props) {
  return (
    <SnackbarProvider
      dense
      maxSnack={5}
      preventDuplicate
      // How long a confirmation dwells, from the token source. It is the one
      // duration in the console that is a reading time rather than a movement,
      // which is why it is `motion.dwell` and not one of the four named
      // motions on the theme.
      autoHideDuration={motionTokens.dwell.confirm}
      // The tone a call site gets when it names none, and most name none: a
      // toast with nothing said about it is reporting that the thing just
      // asked for happened.
      variant="success"
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      Components={TOAST_COMPONENTS}
    >
      {children}
    </SnackbarProvider>
  )
}
